/**
 * BuildInfo — subtle footer badge showing the currently-deployed commit,
 * deploy time, and environment (Dev / Preview / Production).
 *
 * Values come from Vercel's system environment variables, injected at build
 * time via frontend/vercel.json buildCommand. In local dev these are undefined,
 * so the component renders a subdued "dev build" indicator.
 *
 * Rendered consistently in every portal (Consumer, Retailer, Admin, Charity)
 * so any app shows at a glance which commit is live.
 */
export default function BuildInfo() {
  const sha = process.env.REACT_APP_GIT_SHA;
  const deployedAt = process.env.REACT_APP_DEPLOYED_AT;
  // VERCEL_ENV is either "production", "preview", or "development"
  const envRaw = process.env.REACT_APP_VERCEL_ENV;

  // Local dev fallback — keep it honest rather than hiding entirely
  if (!sha && !deployedAt) {
    return (
      <div
        data-testid="build-info-footer"
        className="mt-4 text-center text-xs text-gray-300 select-none"
      >
        dev build
      </div>
    );
  }

  const shortSha = sha ? sha.slice(0, 7) : 'unknown';
  const prettyDate = deployedAt
    ? new Date(deployedAt).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      })
    : 'unknown';

  const envLabel = envRaw
    ? envRaw.charAt(0).toUpperCase() + envRaw.slice(1)
    : 'Unknown';

  // Color code the env so Production stands apart from Preview at a glance
  const envColor =
    envRaw === 'production'
      ? 'text-emerald-600'
      : envRaw === 'preview'
      ? 'text-amber-600'
      : 'text-gray-400';

  return (
    <div
      data-testid="build-info-footer"
      className="mt-4 text-center text-xs text-gray-400 select-none"
      title={sha ? `Commit ${sha}` : undefined}
    >
      <span className={`${envColor} font-medium`}>{envLabel}</span>
      &nbsp;·&nbsp;
      <span className="font-mono text-gray-500">{shortSha}</span>
      &nbsp;·&nbsp;
      <span>{prettyDate}</span>
    </div>
  );
}
